import type { SeriesConfig } from '@/features/telemetry/mappers/toDeviceMetrics';
import type { TelemetrySeriesPoint } from '@/features/telemetry/services/schemas';

export type ChartPoint = { ts: number } & Record<string, number | null>;

/**
 * Projects raw telemetry buckets onto exactly the numeric series a chart needs to draw:
 * a Unix-ms timestamp (recharts' `scale="time"` wants a number, not an ISO string) and
 * one value per configured series, in ascending time order.
 *
 * A metric that's missing or non-numeric on a given bucket becomes `null` rather than
 * `0` — with `connectNulls={false}` on the chart, that reads as a gap instead of a
 * (misleading) dip to zero.
 */
export function toChartPoints(
  points: TelemetrySeriesPoint[],
  series: SeriesConfig[]
): ChartPoint[] {
  return points
    .map((point) => {
      const ts = Date.parse(point.time);
      const value: ChartPoint = { ts };

      for (const config of series) {
        const raw = point[config.key];
        value[config.key] = typeof raw === 'number' ? raw : null;
      }

      return value;
    })
    .filter((point) => !Number.isNaN(point.ts))
    .sort((a, b) => a.ts - b.ts);
}
